import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface EstoqueModelo {
  modelo_slug: string;
  quantidade: number;
  data_foto: string;
}

export interface AparelhosKpiTopo {
  /** Modelos com aparelho no depósito hoje — é o número do chip. */
  modelosComEstoque: number;
  /** Unidades somadas, para a frase do hover. */
  unidades: number;
  /** Data da foto de estoque (YYYY-MM-DD) e há quantos dias ela é. */
  dataFoto: string | null;
  diasDaFoto: number | null;
  /** Os mais disponíveis, para a espiada do hover. */
  topModelos: { nome: string; quantidade: number }[];
}

/**
 * Aparelhos disponíveis hoje — para o chip "Aparelhos" do topo.
 *
 * Douglas 09/08: o consultor precisava abrir Configurações ▸ Produtos ▸ Aparelhos
 * pra saber o que tem em estoque — tela de gestor, longe de onde ele atende.
 * O chip põe isso na barra de sempre, e o clique abre a lista por cima da tela
 * em que ele já está (mesmo desenho do chip "Bio").
 *
 * ⚠️ O número é MODELO COM ESTOQUE, não o total do catálogo. São 134 modelos na
 * tabela do mês e só ~39 com aparelho no depósito: um chip dizendo 134 prometia
 * o que a Prime não entrega hoje.
 *
 * Sem polling curto de propósito — a foto do estoque é importada uma vez por dia.
 */
export function useAparelhosKpiTopo() {
  const { data } = useQuery({
    queryKey: ['aparelhos-kpi-topo'],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<AparelhosKpiTopo> => {
      const { data: estoque, error } = await supabase
        .from('v_aparelhos_estoque_modelo' as never)
        .select('modelo_slug, quantidade, data_foto');
      if (error) throw error;

      const linhas = ((estoque || []) as unknown as EstoqueModelo[]).filter((e) => e.quantidade > 0);
      const dataFoto = linhas[0]?.data_foto ?? null;

      // O catálogo tem o nome bonito do modelo; o estoque só o slug. Só busco os
      // nomes dos que TÊM estoque — o hover mostra 5, não os 134.
      const maisCheios = [...linhas].sort((a, b) => b.quantidade - a.quantidade).slice(0, 5);
      let topModelos: { nome: string; quantidade: number }[] = [];
      if (maisCheios.length > 0) {
        const { data: nomes } = await supabase
          .from('planos_proposta_catalogo' as never)
          .select('codigo, nome')
          .eq('categoria', 'aparelhos')
          .in('codigo', maisCheios.map((m) => m.modelo_slug));
        const mapa = new Map(
          ((nomes || []) as unknown as { codigo: string; nome: string }[]).map((n) => [n.codigo, n.nome]),
        );
        topModelos = maisCheios.map((m) => ({
          nome: mapa.get(m.modelo_slug) ?? m.modelo_slug,
          quantidade: m.quantidade,
        }));
      }

      return {
        modelosComEstoque: linhas.length,
        unidades: linhas.reduce((s, e) => s + e.quantidade, 0),
        dataFoto,
        diasDaFoto: dataFoto
          ? Math.floor((Date.now() - new Date(`${dataFoto}T12:00:00`).getTime()) / 86_400_000)
          : null,
        topModelos,
      };
    },
  });

  return data ?? {
    modelosComEstoque: 0, unidades: 0, dataFoto: null, diasDaFoto: null, topModelos: [],
  };
}
